import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { Content, TopicArea } from '../../types';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../../utils/fileUtils';
import { getTopicAreas } from '../../api/topicAreaApi';
import TagAutocomplete from '../Tag/TagAutocomplete';
import CategoryAutocomplete from '../UI/CategoryAutocomplete';
import { useAuth } from '../../hooks/useAuth';

interface ContentFormProps {
  initialData?: Content;
  onSuccess?: () => void;
  isEdit?: boolean;
  defaultTopicAreaId?: number;
}

const ContentForm: React.FC<ContentFormProps> = ({ initialData, onSuccess, isEdit = false, defaultTopicAreaId }) => {
  const navigate = useNavigate();
  const { user, isAdmin, isEditor } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [lnks, setLnks] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [category1Id, setCategory1Id] = useState<number | ''>('');
  const [category2Id, setCategory2Id] = useState<number | ''>('');
  const [category3Id, setCategory3Id] = useState<number | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [topicAreas, setTopicAreas] = useState<TopicArea[]>([]);
  const [topicAreaId, setTopicAreaId] = useState<number | ''>('');

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setTimestamp(initialData.timestamp ? new Date(initialData.timestamp).toISOString().slice(0, 16) : '');
      setLnks(initialData.lnks || '');
      setCategory1Id(initialData.main_category1_id || '');
      setCategory2Id(initialData.main_category2_id || '');
      setCategory3Id(initialData.main_category3_id || '');
      
      // 기존 태그 로드
      if (initialData.id) {
        const fetchTags = async () => {
          try {
            const res = await axiosInstance.get(`/api/tags/content/${initialData.id}`);
            setTags(res.data.map((tag: any) => tag.name));
          } catch (err) {
            console.error('태그 로딩 실패:', err);
          }
        };
        fetchTags();
      }
    }
  }, [initialData]);

  useEffect(() => {
    const fetchTopicAreas = async () => {
      try {
        const isOnlyMine = !!(user && !isAdmin() && !isEditor());
        const data = await getTopicAreas(isOnlyMine);
        setTopicAreas(data);
      } catch (err) {
        console.error('주제영역 로딩 실패:', err);
      }
    };
    fetchTopicAreas();
  }, [user, isAdmin, isEditor]);

  // defaultTopicAreaId prop 또는 initialData에서 주제영역 초기값 설정
  useEffect(() => {
    if (initialData?.topic_area_id) {
      setTopicAreaId(initialData.topic_area_id);
    } else if (defaultTopicAreaId) {
      setTopicAreaId(defaultTopicAreaId);
    } else if (topicAreas.length > 0) {
      setTopicAreaId(topicAreas[0].id); // 최신 1건 기본 선택
    }
  }, [initialData, defaultTopicAreaId, topicAreas]);

  const selectedCategoryIds = [category1Id, category2Id, category3Id].filter(
    (id): id is number => typeof id === 'number'
  );

  const handleCategoryChange = (ids: number[]) => {
    setCategory1Id(ids[0] || '');
    setCategory2Id(ids[1] || '');
    setCategory3Id(ids[2] || '');
  };

  // 파일 드래그 앤 드롭 처리
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  // 파일 선택 처리
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...selectedFiles]);
      e.target.value = ''; // input 초기화
    }
  };

  // 개별 파일 삭제
  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicAreaId) {
      setMessage('주제영역을 선택해주세요. (필수)');
      return;
    }
    try {
      if (isEdit && initialData) {
        // 수정 모드
        await axiosInstance.put(`/api/contents/${initialData.id}`, { 
          title, 
          description, 
          timestamp, 
          lnks,
          main_category1_id: category1Id || null,
          main_category2_id: category2Id || null,
          main_category3_id: category3Id || null,
          topic_area_id: topicAreaId || null,
          tags
        });
        const contentId = initialData.id;
        
        // 새 파일이 업로드된 경우에만 파일 업로드
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId.toString());
          formData.append('description', '');
          await axiosInstance.post('/api/attach', formData, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
        }
        
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(`/content/${contentId}`);
        }
      } else {
        // 등록 모드
        const res = await axiosInstance.post('/api/contents', { 
          title, 
          description, 
          timestamp, 
          lnks,
          main_category1_id: category1Id || null,
          main_category2_id: category2Id || null,
          main_category3_id: category3Id || null,
          topic_area_id: topicAreaId || null,
          tags
        });
        const contentId = res.data.id;
        
        // 파일 업로드
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId);
          formData.append('description', '');
          await axiosInstance.post('/api/attach', formData, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
        }
        
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(topicAreaId ? `/archives?topicAreaId=${topicAreaId}` : '/archives');
        }
      }
    } catch (err) {
      setMessage(isEdit ? '수정 중 오류 발생!' : '등록 중 오류 발생!');
    }
  };

  return (
    <div style={{ background: 'var(--surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: 24 }}>
      <h2 style={{ marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>{isEdit ? '역사 기록 수정' : '새 역사 기록 등록'}</h2>
      
      <form onSubmit={handleSubmit}>
        {/* 주제영역 선택 (필수, 최상단) */}
        <div style={{ marginBottom: 20, padding: 16, backgroundColor: 'var(--primary-light)', borderRadius: 8, border: '2px solid var(--primary)' }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-primary)' }}>
            📌 주제영역 * <span style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 'normal' }}>(필수 선택)</span>
          </label>
          <select
            value={topicAreaId}
            onChange={e => setTopicAreaId(e.target.value === '' ? '' : Number(e.target.value))}
            required
            className="modern-select"
            style={{
              border: topicAreaId ? '1px solid var(--primary)' : '2px solid var(--danger)',
              fontWeight: topicAreaId ? 'bold' : 'normal',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--surface)'
            }}
          >
            <option value=''>-- 주제영역을 선택하세요 --</option>
            {topicAreas.map(area => (
              <option key={area.id} value={area.id}>{area.title}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            제목 *
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="modern-input"
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            설명 *
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="modern-textarea"
            style={{ minHeight: 120 }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            날짜 및 시간 *
          </label>
          <input
            type="datetime-local"
            value={timestamp}
            onChange={e => setTimestamp(e.target.value)}
            className="modern-input"
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            관련링크 (여러 개인 경우 ;로 구분)
          </label>
          <input
            type="text"
            value={lnks}
            onChange={e => setLnks(e.target.value)}
            placeholder="https://example.com;https://example2.com"
            className="modern-input"
          />
        </div>

        {/* 주제영역 선택 - 상단으로 이동됨 (이 위치는 제거) */}

        {/* 카테고리 선택 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            카테고리 (각각 독립적으로 선택 가능)
          </label>
          <CategoryAutocomplete
            selectedIds={selectedCategoryIds}
            onChange={handleCategoryChange}
            maxSelections={3}
          />
        </div>

        {/* 태그 입력 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            태그 (자동완성 제안 및 엔터/쉼표 입력 가능)
          </label>
          <TagAutocomplete selectedTags={tags} onChange={setTags} />
        </div>

        <div style={{ marginBottom: 30 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: 'var(--text-secondary)' }}>
            첨부파일
          </label>
          
          {/* 드래그 앤 드롭 영역 */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragOver ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: 8,
              padding: 20,
              textAlign: 'center',
              backgroundColor: isDragOver ? 'var(--primary-light)' : 'var(--background)',
              color: 'var(--text-secondary)',
              marginBottom: 12,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>
              📁 파일을 드래그 앤 드롭하거나 클릭하여 선택하세요
            </div>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="fileInput"
            />
            <button
              type="button"
              onClick={() => document.getElementById('fileInput')?.click()}
              className="modern-btn modern-btn-primary"
              style={{ padding: '8px 16px', fontSize: 14 }}
            >
              파일 선택
            </button>
          </div>

          {/* 선택된 파일 목록 */}
          {files.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 12, backgroundColor: 'var(--surface)' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, color: 'var(--text-primary)' }}>
                선택된 파일 ({files.length}개)
              </div>
              {files.map((file, index) => {
                const fileInfo = getFileTypeInfo(file.name, file.type);
                return (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: 'var(--background)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    marginBottom: 8
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <span style={{ fontSize: '20px', marginRight: 8 }}>
                        {fileInfo.icon}
                      </span>
                      <div>
                        <div style={{ fontWeight: '500', color: fileInfo.color }}>
                          {truncateFileName(file.name, 30)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {fileInfo.label} • {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="modern-btn"
                      style={{
                        padding: '4px 8px',
                        backgroundColor: 'var(--danger)',
                        color: 'white',
                        fontSize: '12px',
                        borderRadius: 4
                      }}
                    >
                      삭제
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/content/${initialData?.id}` : '/')}
            className="modern-btn modern-btn-secondary"
          >
            취소
          </button>
          <button
            type="submit"
            className="modern-btn modern-btn-primary"
          >
            {isEdit ? '수정하기' : '등록하기'}
          </button>
        </div>

        {message && (
          <div style={{ 
            marginTop: 16, 
            padding: 12, 
            backgroundColor: 'var(--danger-light)', 
            color: 'var(--danger)', 
            border: '1px solid var(--danger)', 
            borderRadius: 4 
          }}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};
export default ContentForm; 
